import { cdnImages } from "./caseStudyImgMap.cdn";

export interface SubProject {
  heading: string;
  brief: string;
}

interface Stat {
  metric: string;
  stats: string;
}

export interface ImpactData {
  bgColor: string;
  video?: string;
  text: string;
  stats: Stat[];
}

export interface ProcessData {
  bgGradient: string;
  heading: string;
  subheading: string;
  steps: string[];
  processImages: string[];
}

interface LearningData {
  title: string;
  description: string;
}

export interface KeyLearningsData {
  heading: string;
  img: string;
  learnings: LearningData[];
}

export interface AwardData {
  award: string;
  image: string;
}

export interface FeaturedData {
  title: string;
  link: string;
}

export interface TestimonialData {
  text: string;
  name: string;
  designation: string;
}

export interface GalleryData {
  galleryImages: string[];
  quote: string;
}

export interface IProject {
  id: string;
  media: {
    thumbnailVideo: string;
    thumbnail: string;
    cover: string;
  };
  subTitle?: string;
  title: string;
  address: string;
  keywords: string[];
  tagline: string;
  subheaderImage?: string;
  clients: string[];
  partner: string;
  deliverable: string;
  subProjects: { content: SubProject[]; images: string[] };
  impact: ImpactData;
  process: ProcessData;
  keyLearnings: KeyLearningsData;
  awards?: AwardData[];
  featured?: FeaturedData[];
  testimonials: TestimonialData;
  gallery: GalleryData;
}

export const mockProjects: IProject[] = [
  {
    id: "mahakumbh-2025",
    media: {
      thumbnailVideo: cdnImages.caseStudy.coverVid.caseStudy1,
      thumbnail: cdnImages.caseStudy.thumbnails.caseStudy1,
      cover: cdnImages.caseStudy.coverImg.caseStudy1,
    },
    subTitle: "Digital Experience Centre",
    title: "Mahakumbh 2025",
    address: "Prayagraj, Uttar Pradesh",
    keywords: ["Tech + Innovation", "Case Study", "ethos", "definitive"],
    tagline:
      "A first-of-its-kind spiritual experience that reimagined ancient mythology and rituals through immersive technology and cultural storytelling.",
    subheaderImage: cdnImages.caseStudy.subHeader.caseStudy1,
    clients: ["Prayagraj Mela Pradhikaran (Govt of UP)"],
    partner: "Pavilions & Interiors",
    deliverable:
      "We created a fully immersive Digital Experience Centre at Maha Kumbh 2025, blending spatial design, original multimedia content, and interactive technology. Located beside Bade Hanuman Ji Mandir, it featured 13+ thematic zones connected by the narrative “Tirth – A Spiritual Crossing.” From projection mapping and VR to LiDAR murals and tactile artefacts, every element was designed to deliver a culturally rich, emotionally resonant experience.",
    subProjects: {
      content: [
        {
          heading: "What if mythology and ritual came alive through tech?",
          brief:
            "We conceptualized the narrative spine “Tirth”—a metaphysical crossing brought to life through 13 interconnected zones—each designed to immerse visitors in the spiritual and cultural depth of the Kumbh. Our team designed and developed over 11 experiential installations using a blend of cutting-edge technologies including VR, AR, projection mapping, holography, and interactive AV. We produced original content across all mediums, including scripts, voiceovers, 3D environments, motion graphics, and kinetic installations. Physical and digital storytelling were seamlessly integrated through sculptural murals, LED cosmic visuals, and participatory rituals like the interactive Deep Daan. From architectural planning to on-site execution, testing, and visitor flow management, we handled the entire project lifecycle end-to-end.",
        },
      ],
      images: [
        cdnImages.caseStudy.subProjects.caseStudy1.img1,
        cdnImages.caseStudy.subProjects.caseStudy1.img2,
      ],
    },
    impact: {
      bgColor: "#3F0302",
      text: "Where engagement met emotion—bringing the power of mythology to life at scale.",
      video: cdnImages.caseStudy.impact.caseStudy1, // Using the provided video for Kia
      stats: [
        {
          metric: "No. of Visitors",
          stats: "1M+",
        },
        {
          metric: "New tech experiences",
          stats: "10+",
        },
        {
          metric: "Days to execute",
          stats: "21",
        },
      ],
    },
    process: {
      bgGradient: "linear-gradient(159deg, #910000 -7.74%, #090909 88.04%)",
      heading:
        "From mythology to movement, every step fused design, emotion, and immersive storytelling.",
      subheading:
        "The journey began with Creative Intelligence, where emotion, narrative, and innovation came together. In the Ideation phase, we conceptualized Tirth – A Spiritual Crossing, anchoring the experience in mythology, rituals, and symbolism. Through deep thematic curation, we ensured every element resonated intellectually, emotionally, and spiritually.\n\nIn Design and Stories in Spaces, we transformed the narrative into 13 immersive zones. Each space was purposefully crafted with symbolic architecture, motion visuals, and interactive storytelling—from cosmic themes to ritualistic acts—inviting visitors into a deeply layered cultural journey.\n\nFinally, in Fabrication and Tech Integration, we brought the vision to life through LiDAR murals, projection mapping, AR/VR, holography, and tactile elements. Every detail—from lighting to audio—was orchestrated to create a seamless, immersive experience rooted in India’s sacred traditions.",
      steps: [
        "We began by decoding the spiritual and mythological universe of the Kumbh Mela—identifying core themes, emotional arcs, and symbolic touchpoints to form the foundation of the narrative.",
        "Concepts were translated into immersive spatial layouts, interactive flows, and multi-sensory formats—balancing tradition with technology, and storytelling with scale.",
        "We crafted emotionally resonant narratives that lived within the space—using architecture, light, sound, and movement to build meaning across every corner.",
        "Each element—from statues to digital canvases—was brought to life with precision, blending hand-crafted artistry with modular installation techniques suited for a temporary yet high-impact environment.",
        "All storytelling layers—projection mapping, holography, AR/VR, lighting, and audio—were seamlessly embedded into the spatial design, ensuring unified, glitch-free experiences across zones.",
      ],
      processImages: [
        cdnImages.caseStudy.process.caseStudy1.img1,
        cdnImages.caseStudy.process.caseStudy1.img2,
        cdnImages.caseStudy.process.caseStudy1.img3,
        cdnImages.caseStudy.process.caseStudy1.img4,
        cdnImages.caseStudy.process.caseStudy1.img5,
      ],
    },
    keyLearnings: {
      heading:
        "Seamless integration beats spectacle — emotional resonance came not from flashy tech, but from how invisibly it blended with space and ritual.",
      img: cdnImages.caseStudy.keyLearnings.caseStudy1,
      learnings: [
        {
          title: "Cultural Tech Benchmark",
          description:
            "Set a new standard for how immersive technologies can be integrated into large-scale cultural activations with sensitivity and scale.",
        },
        {
          title: "Emotion-Led Storytelling",
          description:
            "Proved that designing for emotional resonance can deepen public engagement and spiritual reflection in civic spaces.",
        },
        {
          title: "Tech with Respect",
          description:
            "Showcased how VR, AR, and interactive mediums can honor sacred narratives while enhancing accessibility and understanding.",
        },
        {
          title: "Replicable Framework",
          description:
            "Established a modular, scalable model for future religious and civic experience centres across India and beyond.",
        },
        {
          title: "Seamless Collaboration",
          description:
            "Enhanced interdisciplinary coordination between creative, technical, and architectural teams to deliver at speed and scale.",
        },
        {
          title: "Heritage Meets Innovation",
          description:
            "Demonstrated how mythology and tradition can be reimagined through innovation to inspire pride, curiosity, and participation.",
        },
      ],
    },
    awards: [
      {
        award:
          "**Gold** at Afaqs marketers Excellence Awards 2025 Design- best spatial design",
        image: cdnImages.caseStudy.awards.caseStudy1.award1, // Using placeholder image
      },
      {
        award:
          "**Gold** at EEMA Spotlight Awards 2025 for Best use of multimedia techology",
        image: cdnImages.caseStudy.awards.caseStudy1.award2,
      },
      {
        award: "**Gold** at EEMA Spotlight Awards 2025 for Best AV production",
        image: cdnImages.caseStudy.awards.caseStudy1.award3,
      },
      {
        award:
          "**Bronze**  at WOW Awards 2025 Achievement in Audio-visual film for an event",
        image: cdnImages.caseStudy.awards.caseStudy1.award4,
      },
      {
        award:
          "**Bronze** at WOW Awards 2025 for Innovation in virtual reality for an event",
        image: cdnImages.caseStudy.awards.caseStudy1.award5,
      },
      {
        award:
          "**Gold** at WOW Awards 2025 for AR/vR Technologies and content for an event",
        image: cdnImages.caseStudy.awards.caseStudy1.award6,
      },
      {
        award:
          "**Gold** at WOW Awards 2025 Special event of the year for govt/ federation/ Association (Upto 10Cr)",
        image: cdnImages.caseStudy.awards.caseStudy1.award7,
      },
      {
        award:
          "**Gold** at EEMAxGLOBAL Awards 2025 for installation/Government show of the year",
        image: cdnImages.caseStudy.awards.caseStudy1.award8,
      },
      {
        award:
          "**Gold** at EEMAxGLOBAL Awards 2025 for Celebratory event of the year",
        image: cdnImages.caseStudy.awards.caseStudy1.award9,
      },
      {
        award:
          "**Gold** at EEMAxGLOBAL Awards 2025 for B2C exhibition of the year",
        image: cdnImages.caseStudy.awards.caseStudy1.award10,
      },
      {
        award:
          "**Gold** at EEMAxGLOBAL Awards 2025 for Government Event of the year - Large",
        image: cdnImages.caseStudy.awards.caseStudy1.award11,
      },
      {
        award: "**Silver** at EEMAxGLOBAL Awards 2025 for Debut IP of the year",
        image: cdnImages.caseStudy.awards.caseStudy1.award6,
      },
    ],
    testimonials: {
      text: "“The Digital Mahakumbh Centre exceeded our expectations—bridging deep spirituality with world-class innovation. It became a focal point of the Mela, engaging everyone from saints to students. A timeless vision brought to life with absolute brilliance.”",
      name: "— Arvind Kumar,",
      designation: "IAS, Commissioner,\n\nPrayagraj Mela Authority",
    },
    gallery: {
      galleryImages: [
        cdnImages.caseStudy.gallery.caseStudy1.gallery1,
        cdnImages.caseStudy.gallery.caseStudy1.gallery2,
        cdnImages.caseStudy.gallery.caseStudy1.gallery3,
        cdnImages.caseStudy.gallery.caseStudy1.gallery4,
        cdnImages.caseStudy.gallery.caseStudy1.gallery5,
        cdnImages.caseStudy.gallery.caseStudy1.gallery6,
        cdnImages.caseStudy.gallery.caseStudy1.gallery7,
        cdnImages.caseStudy.gallery.caseStudy1.gallery8,
        cdnImages.caseStudy.gallery.caseStudy1.gallery9,
      ],
      quote:
        '"Great things don\'t come from great minds alone — they come from great minds moving as one."',
    },
  },
  {
    id: "supreme-court",
    media: {
      thumbnailVideo: cdnImages.caseStudy.coverVid.caseStudy2,
      thumbnail: cdnImages.caseStudy.thumbnails.caseStudy2,
      cover: cdnImages.caseStudy.coverImg.caseStudy2,
    },
    subTitle: "Digital Experience Centre",
    title: "NJMA SUPREME COURT",
    address: "Prayagraj, Uttar Pradesh",
    keywords: ["Tech + Innovation", "Case Study", "ethos", "definitive"],
    tagline:
      "An unprecedented legal experience that brings India’s judicial legacy to life through immersive technology and narrative design.",
    subheaderImage: cdnImages.caseStudy.subHeader.caseStudy2,
    clients: ["Supreme Court of India"],
    partner: "Moving Pixels",
    deliverable:
      "The Museum of the Supreme Court of India redefines legal education through immersive storytelling and interactive technology. Designed by Tagglabs, it transforms judicial history into engaging, accessible experiences — bridging constitutional foundations with contemporary legal discourse.",
    subProjects: {
      content: [
        {
          heading: "What Happens When Design Meets Justice?",
          brief:
            "Tagglabs’ design for the Museum of the Supreme Court of India redefines legal education through immersive storytelling and interactive technology. More than a historical archive, the museum becomes a dynamic learning environment that bridges constitutional roots with contemporary legal challenges.\n\nReplacing dense legal text with experiential formats, the space serves as a central hub for young lawyers, students, and the public. Virtual courtrooms simulate real-life proceedings, interactive exhibits break down landmark judgments, and Vidhik — an AI-powered guide — narrates complex legal ideas in human-centered, engaging ways.\n\nAugmented artefacts, tactile installations, and carefully curated spatial storytelling allow visitors to explore, touch, and interact — turning passive observation into active discovery. Every element emphasizes accessibility, inclusivity, and engagement, making law both rigorous and relatable.",
        },
      ],
      images: [
        cdnImages.caseStudy.subProjects.caseStudy2.img1,
        cdnImages.caseStudy.subProjects.caseStudy2.img2,
      ],
    },
    impact: {
      bgColor: "#856649",
      text: "From Footfall to Fulfilment — A Museum Experience That Redefined Engagement, Learning, and Public Connection with the Law",
      video: cdnImages.caseStudy.impact.caseStudy2, // Using the provided video for Kia
      stats: [
        {
          metric: "Lawyers visited the museum",
          stats: "5k+",
        },
        {
          metric: "Pages digitized",
          stats: "10k+",
        },
        {
          metric: "AI Lawyer, a viral sensation",
          stats: "1st",
        },
      ],
    },
    process: {
      bgGradient: "linear-gradient(159deg, #A35A16 -7.74%, #40240A 88.04%)",
      heading:
        "From legal texts to living experiences, how did design turn the law into a story worth stepping into?",
      subheading:
        "Bringing the Museum of the Supreme Court to life was a layered, collaborative process. We began with deep research — engaging legal scholars, curators, and educators to uncover narratives that resonate. From there, we crafted an experience blueprint that merged historical accuracy with emotional storytelling. The design phase focused on building intuitive journeys through zones, replacing text-heavy formats with dynamic visual and interactive elements. We developed immersive modules like virtual courtrooms and landmark case explorations, powered by Vidhik — an AI guide built to simplify complex legal ideas. Prototyping and user-testing ensured accessibility and engagement across age groups and backgrounds. The final step was integration — where spatial, digital, and content design came together to deliver a seamless, participatory museum experience that invites every visitor to not just witness, but engage with the journey of Indian law.",
      steps: [
        "Insight & Research We began by conducting in-depth consultations with legal scholars, museum curators, educators, and researchers to understand the judicial narrative and user expectations. This phase helped define content priorities, audience personas, and the tone of experience.",
        "Narrative & Experience Design Building on the insights, we mapped the visitor journey through thematic zones that mirrored the evolution of India’s legal system. We replaced text-heavy formats with immersive storytelling, interactive installations, and intuitive user flows to ensure emotional and intellectual engagement.",
        "Technology Development Core digital components were developed, including virtual courtrooms, interactive timelines, and Vidhik — an AI-powered legal storytelling guide. These tools were designed to make complex legal ideas approachable, engaging, and dynamic.",
        "Prototyping & Testing We created and iterated on physical layouts, digital UI elements, and interaction models. Prototypes were tested with law students, general visitors, and accessibility experts to ensure clarity, inclusivity, and relevance.",
        "Deployment & Integration Final installation brought together spatial design, storytelling, and technology into a cohesive experience. Every element — from touchscreens to lighting — was integrated and aligned to create a seamless, participatory environment that invites exploration and reflection.",
      ],
      processImages: [
        cdnImages.caseStudy.process.caseStudy2.img1,
        cdnImages.caseStudy.process.caseStudy2.img2,
        cdnImages.caseStudy.process.caseStudy2.img3,
        cdnImages.caseStudy.process.caseStudy2.img4,
        cdnImages.caseStudy.process.caseStudy2.img5,
      ],
    },
    keyLearnings: {
      heading:
        "Immersion thrives in clarity — the most powerful moments emerged not from complexity, but from how simply design translated law into human experience.",
      img: cdnImages.caseStudy.keyLearnings.caseStudy2,
      learnings: [
        {
          title: "Scalable Model for Legal Education",
          description:
            "The museum has become a reference point for other judicial and civic institutions exploring interactive, design-led learning environments.",
        },
        {
          title: "Expansion of Vidhik",
          description:
            "The AI-powered guide Vidhik is now being explored for integration into legal education platforms and institutional learning programs.",
        },
        {
          title: "State-Level Interest",
          description:
            "Several state courts and law universities have expressed interest in replicating the format for regional judicial storytelling and outreach.",
        },
        {
          title: "Civic Engagement Through Design",
          description:
            "The project has initiated new dialogues on using design and technology to democratize access to complex civic and legal knowledge.",
        },
        {
          title: "A New Benchmark for Public Institutions",
          description:
            "The museum stands as a precedent for how public-sector spaces can evolve into participatory, emotionally resonant experiences.",
        },
      ],
    },
    testimonials: {
      text: "“The Supreme Court Museum was conceptualized to bring the journey of Indian justice closer to the people. Tagglabs has not only met but exceeded this vision with a stunning blend of storytelling and technology. This museum doesn’t just inform — it inspires.”",
      name: "— Arvind Kumar,",
      designation: "IAS, Commissioner,\n\nPrayagraj Mela Authority",
    },
    gallery: {
      galleryImages: [
        cdnImages.caseStudy.gallery.caseStudy2.gallery1,
        cdnImages.caseStudy.gallery.caseStudy2.gallery2,
        cdnImages.caseStudy.gallery.caseStudy2.gallery3,
        cdnImages.caseStudy.gallery.caseStudy2.gallery4,
        cdnImages.caseStudy.gallery.caseStudy2.gallery5,
        cdnImages.caseStudy.gallery.caseStudy2.gallery6,
        cdnImages.caseStudy.gallery.caseStudy2.gallery7,
        cdnImages.caseStudy.gallery.caseStudy2.gallery8,
        cdnImages.caseStudy.gallery.caseStudy2.gallery9,
      ],
      quote:
        "“A visual journey through form, story, and space that brought the spirit of justice alive inside the Supreme Court Museum.”",
    },
  },
  {
    id: "invest-mp",
    media: {
      thumbnailVideo: cdnImages.caseStudy.coverVid.caseStudy3,
      thumbnail: cdnImages.caseStudy.thumbnails.caseStudy3,
      cover: cdnImages.caseStudy.coverImg.caseStudy3,
    },
    subTitle: "Digital Experience Zone",
    title: "INVEST MP SUMMIT",
    address: "Madhya Pradesh",
    keywords: ["Tech + Innovation", "Case Study", "ethos", "definitive"],
    tagline:
      "A pioneering pavilion that blended tradition and innovation to narrate Madhya Pradesh’s story through sensory design and digital immersion.",
    subheaderImage: cdnImages.caseStudy.subHeader.caseStudy3,
    clients: ["Govt of Madhya Pradesh"],
    partner: "Meroform",
    deliverable:
      "We delivered a 5,000 sq. ft. immersive pavilion with 22 tech-enabled exhibits, featuring AI, VR, MR, and gesture-based experiences. Key highlights included the Anamorphic Wall, Interactive Forest, and #MyMPStory. The scope covered concept, content, design, tech integration, and social amplification.",
    subProjects: {
      content: [
        {
          heading: "How Did MP Blend Culture with Innovation?",
          brief:
            "The Madhya Pradesh Experience Zone was a visionary 5,000 sq. ft. immersive pavilion created for the Global Investors Summit 2025 in Bhopal. Designed to move beyond traditional exhibition formats, the pavilion redefined how Indian states present themselves on global platforms—through emotion-driven storytelling and cutting-edge technology.\n\nAnchored by the theme “Confluence – The Sacred Meeting of Heritage and Progress”, the experience brought together the state’s cultural richness and its vision for the future. Visitors were taken on a multi-sensory journey through three curated narrative corridors, exploring everything from ancient sites like Bhimbetka to the latest in digital governance and industrial transformation.\n\nWith 22 interactive exhibits powered by technologies such as AI, VR, gesture control, and projection mapping, the pavilion offered a seamless blend of information, emotion, and innovation. It not only engaged lakhs of visitors—including dignitaries and global industry leaders—but also positioned Madhya Pradesh as a frontrunner in citizen-centric governance and future-ready development.",
        },
      ],
      images: [
        cdnImages.caseStudy.subProjects.caseStudy3.img1,
        cdnImages.caseStudy.subProjects.caseStudy3.img2,
      ],
    },
    impact: {
      bgColor: "#296D84",
      text: "From Footfall to Fulfilment — A Museum Experience That Redefined Engagement, Learning, and Public Connection with the Law",
      video: cdnImages.caseStudy.impact.caseStudy3, // Using the provided video for Kia
      stats: [
        {
          metric: "Delegates Explored the Experience",
          stats: "18,736+",
        },
        {
          metric: "Countries, One Platform",
          stats: "60+",
        },
        {
          metric: "Notable Visitors ",
          stats: "Narendra Modi\n\nMohan Yadav\n\nAmit Shah\n\nShri Manohar Lal",
        },
      ],
    },
    process: {
      bgGradient: "linear-gradient(159deg, #61AFC6 0%, #1D3F49 100%)",
      heading: "Behind the Vision: How the MP Experience Zone Took Shape",
      subheading:
        "The execution of the Madhya Pradesh Experience Zone was a meticulously orchestrated collaboration that brought together strategy, storytelling, design, and technology in perfect harmony. The process began with a deep understanding of the state’s heritage, aspirations, and development vision, which shaped a clear narrative framework. Every phase—from conceptualization and theme-building to spatial planning, content creation, and on-ground deployment—was approached with precision to ensure the pavilion was far more than an exhibition space. It was designed to be an emotionally resonant journey that celebrated Madhya Pradesh’s legacy while projecting its future-ready identity. The team carefully prioritized immersive engagement at every touchpoint, blending cutting-edge technology with rich cultural references to connect with diverse audiences. This holistic approach also focused on innovation and scalability, setting a new benchmark for how states can craft public experiences that are meaningful, memorable, and capable of leaving a lasting impact well beyond the event itself.",
      steps: [
        "Aligning Vision & Goals We began by conducting in-depth workshops with key state stakeholders to understand Madhya Pradesh’s development priorities and cultural essence. The team identified core narratives aligned with the vision of Viksit Bharat 2047 and defined clear experience goals—emotional impact, multi-audience relevance, and future-readiness.",
        "Designing the Narrative With the theme “Confluence – The Sacred Meeting of Heritage and Progress” as the foundation, we mapped out a narrative journey across three immersive corridors. Each storyline was carefully curated to interweave history, innovation, governance, and aspiration into a unified and compelling visitor experience.",
        "Planning Space & Exhibits We designed a fluid layout for the 5,000 sq. ft. pavilion, creating intuitive zones that guided visitors seamlessly. The final concept included 22 diverse exhibits powered by technologies like AI, VR, projection mapping, LiDAR, and gesture control, along with iconic installations for emotional anchoring.",
        "Developing Content & Tech Each exhibit was brought to life through detailed scripting, custom content creation, and emotional storytelling. We integrated immersive audio-visuals, motion design, and real-time interactivity using RFID, kinetic walls, AI avatars, and holograms to deliver a layered, high-impact experience.",
        "Building the Experience The pavilion was fabricated using modular structures for efficient setup. Cross-functional teams handled on-site production, lighting, AV, and system integration. Rigorous pre-launch testing ensured all exhibits functioned smoothly and delivered a high-quality experience to every visitor.",
        "Launching & Engaging Audiences Unveiled at the Global Investors Summit 2025, the pavilion was experienced by dignitaries, stakeholders, and lakhs of visitors. Interactive elements like #MyMPStory drove social engagement, while real-time data tracking captured insights into visitor preferences and behavior.",
      ],
      processImages: [
        cdnImages.caseStudy.process.caseStudy3.img1,
        cdnImages.caseStudy.process.caseStudy3.img2,
        cdnImages.caseStudy.process.caseStudy3.img3,
        cdnImages.caseStudy.process.caseStudy3.img4,
        cdnImages.caseStudy.process.caseStudy3.img5,
      ],
    },
    keyLearnings: {
      heading:
        "“People remember what they feel—immersive storytelling creates lasting impressions where facts alone cannot.”",
      img: cdnImages.caseStudy.keyLearnings.caseStudy3,
      learnings: [
        {
          title: "Scalable Design for Continued Use",
          description:
            "The pavilion’s modular architecture and plug-and-play technology approach enabled several of its exhibits—such as the AI Holobox, VR journeys, and digital governance stories—to be adapted for permanent display in museums, government halls, and mobile units across districts.",
        },
        {
          title: "Blueprint for Experiential Governance",
          description:
            "The strategic use of narrative, emotional engagement, and interactive tech formed a replicable framework that can be applied to other government departments or states wishing to present policies, culture, or development journeys in a more engaging manner.",
        },
        {
          title: "Multi-Audience Resonance",
          description:
            "From students and tourists to policymakers and global investors, the experience was designed to speak to multiple demographics simultaneously. This inclusive storytelling approach increased dwell time, satisfaction levels, and long-term recall across visitor categories.",
        },
        {
          title: "Digital Engagement & Shareability",
          description:
            "Interactive features like #MyMPStory enabled visitors to generate personalized digital souvenirs, which were widely shared across social media platforms. This not only amplified reach but also created organic advocacy for the state’s vision and leadership.",
        },
        {
          title: "Institutional Adoption & Interest",
          description:
            "Inspired by the pavilion’s impact, several Madhya Pradesh departments and national-level institutions have expressed interest in adopting similar immersive formats to communicate public initiatives, showcase cultural heritage, and drive citizen awareness campaigns.",
        },
        {
          title: "Enhanced Citizen-Government Connection",
          description:
            "By moving beyond one-way communication to emotionally resonant storytelling, the pavilion fostered a stronger sense of pride, participation, and ownership among citizens—essential for inclusive and future-focused governance.",
        },
      ],
    },
    awards: [
      {
        award:
          "**Gold** at EEMAxGlobal 2025\n\nFor Government event of the year - Small",
        image: cdnImages.caseStudy.awards.caseStudy3.award1, // Using placeholder image
      },
      {
        award:
          "**Bronze** at EEMAxGlobal 2025\n\nFor Installations/government show of the year",
        image: cdnImages.caseStudy.awards.caseStudy3.award2,
      },
    ],
    testimonials: {
      text: "“The Madhya Pradesh Experience Zone truly touched my heart. It wasn’t just an exhibition—it felt like stepping into the spirit of the state. The use of storytelling and technology made the experience unforgettable.”",
      name: "— Rajesh Verma,",
      designation: "Government Representative",
    },
    gallery: {
      galleryImages: [
        cdnImages.caseStudy.gallery.caseStudy3.gallery1,
        cdnImages.caseStudy.gallery.caseStudy3.gallery2,
        cdnImages.caseStudy.gallery.caseStudy3.gallery3,
        cdnImages.caseStudy.gallery.caseStudy3.gallery4,
        cdnImages.caseStudy.gallery.caseStudy3.gallery5,
        cdnImages.caseStudy.gallery.caseStudy3.gallery6,
        cdnImages.caseStudy.gallery.caseStudy3.gallery7,
        cdnImages.caseStudy.gallery.caseStudy3.gallery8,
        cdnImages.caseStudy.gallery.caseStudy3.gallery9,
      ],
      quote:
        "“A visual journey through innovation, culture, and immersive moments that brought Madhya Pradesh’s story to life.”",
    },
  },
  {
    id: "chapekar-wada",
    media: {
      thumbnailVideo: cdnImages.caseStudy.coverVid.caseStudy4,
      thumbnail: cdnImages.caseStudy.thumbnails.caseStudy4,
      cover: cdnImages.caseStudy.coverImg.caseStudy4,
    },
    subTitle: "HISTORIC MUSEUM",
    title: "CHAPEKAR WADA",
    address: "Pune, Maharashtra",
    keywords: ["Tech + Innovation", "Case Study", "ethos", "definitive"],
    tagline:
      "“An immersive museum that fuses heritage and technology to retell the Chapekar brothers’ legacy of courage and sacrifice.”",
    subheaderImage: cdnImages.caseStudy.subHeader.caseStudy4,
    clients: ["Govt of Maharashtra"],
    partner: "Moving Pixels",
    deliverable:
      "“We transformed the Chapekar brothers’ ancestral home into a first-of-its-kind immersive museum, combining rare artefacts, curated heirlooms, holograms, AR/VR, and interactive storytelling. The project spanned end-to-end — from concept development and content curation to spatial design, technology integration, and on-ground fabrication — creating a dynamic space that bridges heritage and innovation.”",
    subProjects: {
      content: [
        {
          heading:
            "How do you turn an ancestral home into a living, breathing museum?",
          brief: `The Museum of the Krantivir Chapekar Bandhu was envisioned as more than a memorial — it was designed to immerse visitors in the spirit of rebellion that sparked India’s freedom struggle. Located within the ancestral home of the Chapekar brothers, the project demanded a delicate balance: preserving historical authenticity while weaving in cutting-edge storytelling technologies.
          \n\nAnchored by the theme “From Sacrifice to Legacy”, the museum brings together rare family artefacts, personal belongings, and original litho press blocks with interactive installations, holographic narrations, and AR/VR journeys that reimagine pivotal moments of the brothers’ lives. Visitors are taken on a multi-sensory path through curated story zones, moving from the intimate world of the Chapekar family to the revolutionary act that shook the British Empire.
          \n\nWith experiences powered by holograms, projection mapping, and gamified learning modules, the museum redefines how India’s freedom history is remembered. It offers not just static information but an immersive dialogue between the past and present, inspiring new generations to reflect on courage, sacrifice, and patriotism.`,
        },
      ],
      images: [
        cdnImages.caseStudy.subProjects.caseStudy4.img1,
        cdnImages.caseStudy.subProjects.caseStudy4.img2,
      ],
    },
    impact: {
      bgColor: "#946A14",
      text: "By blending design, storytelling, and technology, the project left a lasting impression that transcended traditional museum exhibits.",
      video: cdnImages.caseStudy.impact.caseStudy4, // Using the provided video for Kia
      stats: [
        {
          metric: "Lives Touched and Transformed",
          stats: "1M+",
        },
        {
          metric: "Freedom Experience by Numbers",
          stats: "10k+",
        },
        {
          metric: "Learners Guided by Vedas",
          stats: "5K+",
        },
      ],
    },
    process: {
      bgGradient: "linear-gradient(159deg, #946A14 0%, #4F380A 100%)",
      heading:
        "From Ancestral Home to Immersive Museum: The Making of a Legacy",
      subheading:
        "The journey of creating the Museum of the Krantivir Chapekar Bandhu began with in-depth research and collaboration with historians, trustees, and local stakeholders to anchor the project in authenticity. Building on this foundation, we developed the concept and spatial design, mapping a visitor journey that seamlessly transitioned from rare artefacts to immersive digital storytelling. Content was curated with care — preserving heirlooms, litho blocks, and personal belongings while crafting narratives for holograms, AR/VR modules, and interactive exhibits. Technology was then integrated through holographic storytelling, projection mapping, and gamified learning, ensuring history could be experienced dynamically. Finally, the vision was realized on-site through meticulous fabrication and execution, transforming the Chapekar ancestral home into a living, breathing museum where heritage and innovation converge.",
      steps: [
        "Discovery & Research We began by engaging with historians, trustees, and local stakeholders to ensure authenticity. Site visits to the ancestral home helped us identify the core themes of sacrifice, rebellion, and legacy.",
        "Concept Design Our team developed the overarching narrative and mapped the visitor journey through curated story zones. This provided the foundation for a seamless blend of history and immersion.",
        "Art Direction & Spatial Design We created a visual and spatial language that merged the warmth of heritage with the dynamism of interactive storytelling. Each space was designed to guide visitors emotionally and thematically.",
        "Content Curation Rare artefacts, family heirlooms, and litho press blocks were carefully preserved and showcased. Alongside these, we scripted narratives for holograms, AR/VR, and projection-led storytelling.",
        "Technology Integration Immersive experiences such as holograms, AR/VR journeys, projection mapping, and gamified learning were seamlessly embedded into the space. The technology was designed to amplify history rather than overshadow it.",
        "Fabrication & Execution Finally, the vision was realized through meticulous on-ground fabrication and installation. Every detail — from spatial layout to digital calibration — was fine-tuned to deliver a powerful, living museum experience.",
      ],
      processImages: [
        cdnImages.caseStudy.process.caseStudy4.img1,
        cdnImages.caseStudy.process.caseStudy4.img2,
        cdnImages.caseStudy.process.caseStudy4.img3,
        cdnImages.caseStudy.process.caseStudy4.img4,
        cdnImages.caseStudy.process.caseStudy4.img5,
      ],
    },
    keyLearnings: {
      heading:
        "“Authenticity shapes connection—when design and technology honor true stories, they transform spaces into timeless experiences.”",
      img: cdnImages.caseStudy.keyLearnings.caseStudy4,
      learnings: [
        {
          title: "Deepened Community Engagement ",
          description:
            "By working closely with family trustees, local historians, and cultural stakeholders, the project ensured every element was rooted in authenticity, allowing the museum to resonate not only with visitors but also with the community that carries the Chapekar legacy.",
        },
        {
          title: "Human-Centered Storytelling",
          description:
            "Instead of relying solely on facts and timelines, the narrative was crafted around emotions of sacrifice, rebellion, and courage, creating a deeper connection where visitors could feel the weight of history rather than just observe it.",
        },
        {
          title: "Seamless Tech Integration",
          description:
            "Advanced tools such as AR, VR, and holograms were not introduced as standalone attractions but carefully woven into the design, making technology a silent enabler of immersion rather than a distraction from the story.",
        },
        {
          title: "Collaborative Innovation ",
          description:
            "The project thrived on the synergy of concept designers, curators, fabricators, and technologists, showing how interdisciplinary collaboration can transform abstract ideas into impactful, physical experiences.",
        },
        {
          title: "Scalable Model",
          description:
            "The process and methodology used in this project serve as a blueprint for future cultural and heritage spaces, proving that history can be preserved and retold through immersive, interactive formats adaptable to different contexts.",
        },
        {
          title: "Enduring Impact",
          description:
            "Beyond imparting knowledge, the space leaves visitors with a lasting emotional imprint—reminding them that stories of courage and sacrifice live on when they are felt, not just told.",
        },
      ],
    },
    testimonials: {
      text: "“Working with Emotion Design brought a new dimension to our vision. They have beautifully balanced the emotional depth of history with the energy of modern storytelling. The Chapekar brothers’ story now resonates with every visitor — not just as history, but as a living experience.”",
      name: "— Trustee, Krantivir Chapekar",
      designation: "Bandhu Rashtriya Sangrahalaya",
    },
    gallery: {
      galleryImages: [
        cdnImages.caseStudy.gallery.caseStudy4.gallery1,
        cdnImages.caseStudy.gallery.caseStudy4.gallery2,
        cdnImages.caseStudy.gallery.caseStudy4.gallery3,
        cdnImages.caseStudy.gallery.caseStudy4.gallery4,
        cdnImages.caseStudy.gallery.caseStudy4.gallery5,
        cdnImages.caseStudy.gallery.caseStudy4.gallery6,
        cdnImages.caseStudy.gallery.caseStudy4.gallery7,
        cdnImages.caseStudy.gallery.caseStudy4.gallery8,
        cdnImages.caseStudy.gallery.caseStudy4.gallery9,
      ],
      quote:
        "“An evocative narrative that transforms heritage into a living, breathing journey of courage and conviction.”",
    },
  },
];
